/* eslint-disable @typescript-eslint/no-explicit-any */
import { getFullSignature } from 'Modules/Communication/CommunicationSettings/shared/utils/signature-helpers'
import {
  IEmailAttachment,
  IEmailModel,
  ISendingStatusPayload,
} from 'Modules/Communication/Email/shared/models/Email'
import {
  EMAILS_FETCH_NEW_SILENTLY_REQUESTED,
  EMAILS_FETCH_STATISTICS_REQUESTED,
  EMAILS_MARK_AS_REPLIED_OR_FORWARDED,
  EMAILS_REMOVE_FROM_LIST,
  EMAIL_FETCH_SILENT_REQUESTED,
  EMAIL_RESET_CURRENT,
  EMAIL_UPDATE_DRAFT,
} from 'shared/store/communication/emails/actionTypes'
import {
  getCurrentEmail,
  getEmailsFilters,
} from 'shared/store/communication/emails/selectors'
import {
  IInboxFilters,
  IMessageRecipient,
  InboxSectionEnum,
} from 'Modules/Communication/shared/models/Inbox'
import { ISignature } from 'Modules/Communication/shared/models/Signature'
import { call, delay, put, select, take, takeEvery } from 'redux-saga/effects'
import { addBoardContact } from 'shared/api/client.api'
import {
  newEmailPrefix,
  unsupportedEmailFileTypes,
} from 'shared/constants/email'
import { IPatchModel } from 'shared/models/Common'
import { IEmailMessage, ILoadingMessageRecipient } from 'shared/models/Message'
import { getActiveClient } from 'shared/store/clients/selectors'
import { getEmailSignature } from 'shared/store/emailSettings/signature/selectors'
import { getExtension, hasInvalidFileTypes } from 'shared/utils/file'
import {
  getDraftPatchBody,
  getPatchBodyForAllChanges,
  getWrappedEmailContent,
} from 'shared/utils/message'
import {
  SEND_EMAIL_REQUESTED,
  SEND_EMAIL_SUCCESS,
  SEND_EMAIL_ERROR,
  SEND_EMAIL_UPDATE_DRAFT_REQUESTED,
  SEND_EMAIL_CREATE_DRAFT_REQUESTED,
  SEND_EMAIL_CREATE_DRAFT_ERROR,
  SEND_EMAIL_CREATE_DRAFT_SUCCESS,
  SEND_EMAIL_UPDATE_DRAFT_ERROR,
  SEND_EMAIL_UPDATE_DRAFT_SUCCESS,
  SEND_EMAIL_DELETE_DRAFT_REQUESTED,
  SEND_EMAIL_DELETE_DRAFT_SUCCESS,
  SEND_EMAIL_DELETE_DRAFT_ERROR,
  SEND_EMAIL_UPDATE_ATTACHMENTS_REQUESTED,
  SEND_EMAIL_UPDATE_ATTACHMENTS_ERROR,
  SEND_EMAIL_UPDATE_ATTACHMENTS_SUCCESS,
  SEND_EMAIL_REPLY_OR_FORWARD_REQUESTED,
  SEND_EMAIL_REPLY_OR_FORWARD_ERROR,
  SEND_EMAIL_REPLY_OR_FORWARD_SUCCESS,
  SEND_EMAIL_UPDATE_RECIPIENTS_REQUESTED,
  SEND_EMAIL_CREATE_CONTACT_REQUESTED,
  SEND_EMAIL_CREATE_CONTACT_FAILED,
  SEND_EMAIL_CREATE_CONTACT_SUCCESS,
  SEND_EMAIL_UPDATE_RECIPIENTS_SUCCESS,
  SEND_EMAIL_REMOVE_RECIPIENT_REQUESTED,
  SEND_EMAIL_REMOVE_RECIPIENT_SUCCESS,
  SEND_EMAIL_UPDATE_MANY_RECIPIENTS_REQUESTED,
  SEND_EMAIL_UPDATE_MANY_RECIPIENTS_SUCCESS,
  SEND_EMAIL_FETCH_RECIEPT_REQUESTED,
  SEND_EMAIL_FETCH_RECIEPT_ERROR,
  SEND_EMAIL_FETCH_RECIEPT_SUCCESS,
  SEND_EMAIL_UPDATE_SENDING_STATUS_REQUESTED,
  SEND_EMAIL_UPDATE_SENDING_STATUS_SUCCESS,
  SEND_EMAIL_UPDATE_SENDING_STATUS_ERROR,
  SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
  SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
  SEND_EMAIL_REFETCH_SENDING_STATUS_REQUESTED,
  SEND_EMAIL_REFETCH_SENDING_STATUS_SUCCESS,
  SEND_EMAIL_IS_DRAFT_UPDATING,
} from './actionTypes'
import {
  getSendEmail,
  getSendEmailRecipients,
  getEmailReciept as recieptSelector,
  isDraftUpdating,
} from './selectors'
import { SendEmailActionTypes } from './types'
import {
  addEmailAttachments,
  createEmail,
  createForwardDraft,
  createReplyDraft,
  deleteEmailAttachment,
  deleteMessage,
  getEmail,
  getEmailReciept,
  sendMessage,
  updateEmail,
  updateEmailRecipients,
} from 'Modules/Communication/Email/shared/api/emails.api'
import { nanoid } from '@reduxjs/toolkit'
import { addToasterMessage } from 'shared/store/toaster'

function* safelyUpdateEmailSaga(id: string, body: any) {
  while (true) {
    const isUpdatingDraft: boolean = yield select(isDraftUpdating)
    if (!isUpdatingDraft) {
      try {
        yield put({ type: SEND_EMAIL_IS_DRAFT_UPDATING, payload: true })
        yield call(updateEmail, id, body)
        yield put({ type: SEND_EMAIL_IS_DRAFT_UPDATING, payload: false })
      } catch (e: any) {
        console.log(e.response?.data)
        yield put({
          type: SEND_EMAIL_UPDATE_DRAFT_ERROR,
          payload:
            e.response?.data?.Message ||
            'Det skjedde en feil ved oppdatering av utkast',
        })
      }

      return isUpdatingDraft
    }
    yield take()
  }
}

function* sendEmail(action: SendEmailActionTypes) {
  try {
    const { message }: { message: IEmailModel } = yield select(getSendEmail)
    const messageFromForm: IEmailMessage = action.payload.message
    const filters: IInboxFilters = yield select(getEmailsFilters)
    const id: string = action.payload.messageId
    const currentEmailId: string = yield select(getCurrentEmail)

    // Validate draft is updated
    const patchBody = getPatchBodyForAllChanges(messageFromForm, message)

    if (patchBody.length) {
      yield safelyUpdateEmailSaga(id, patchBody)
    }

    yield call(sendMessage, action.payload.messageId)

    yield put({ type: SEND_EMAIL_SUCCESS })
    yield put({ type: SEND_EMAIL_FETCH_RECIEPT_REQUESTED, payload: id })

    if (filters?.queryStatus === InboxSectionEnum.Draft) {
      yield put({
        type: EMAILS_REMOVE_FROM_LIST,
        payload: [id],
      })

      if (currentEmailId === id)
        yield put({
          type: EMAIL_RESET_CURRENT,
          payload: { navigateFromCurrent: true },
        })
    }
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_ERROR,
      payload:
        e.response.data?.Message || 'Det skjedde en feil ved sending av e-post',
    })
  }
}

function* createDraft(action: SendEmailActionTypes): any {
  try {
    const { id } = yield select(getActiveClient)
    const { message } = yield select(getSendEmail)

    const draft = {
      ...message,
      contentHtml: getWrappedEmailContent(action.payload),
    }
    const { data } = yield call(createEmail, id, draft)

    yield put({
      type: SEND_EMAIL_CREATE_DRAFT_SUCCESS,
      payload: { id: data, message: draft },
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_CREATE_DRAFT_ERROR,
      payload:
        e.response.data?.Message ||
        'Det skjedde en feil ved opprettelse av utkast',
    })
  }
}

function* updateDraft(action: SendEmailActionTypes) {
  try {
    yield safelyUpdateEmailSaga(
      action.payload.id,
      getDraftPatchBody(action.payload)
    )

    yield put({
      type: SEND_EMAIL_UPDATE_DRAFT_SUCCESS,
      payload: action.payload,
    })
    yield put({
      type: EMAIL_UPDATE_DRAFT,
      payload: { ...action.payload, id: action.payload.id },
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_UPDATE_DRAFT_ERROR,
      payload:
        e.response?.data?.Message ||
        'Det skjedde en feil ved oppdatering av utkast',
    })
  }
}

function* updateRecipient(action: SendEmailActionTypes) {
  const payload = action.payload as IMessageRecipient

  const loadingItem: ILoadingMessageRecipient[] = [
    {
      // TODO: update to item.id
      id: payload.nameregisterId || payload.email || nanoid(),
      type: 'add',
      recipientType: payload.recipientType,
    },
  ]

  yield put({
    type: SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
    payload: loadingItem,
  })

  try {
    const { messageId }: { messageId: string } = yield select(getSendEmail)

    const recipients: IMessageRecipient[] = yield select(getSendEmailRecipients)

    const isAlreadyRegistered = recipients.some(
      (recipient: IMessageRecipient) =>
        recipient.email?.toLowerCase() === payload.email?.toLowerCase() &&
        recipient.recipientType === payload.recipientType
    )

    if (payload.email && isAlreadyRegistered) return

    // If in draft mode
    if (messageId) {
      const updateObj = {
        op: 'add',
        path: '/recipients',
        value: [payload],
      } as IPatchModel
      yield call(updateEmailRecipients, messageId, [updateObj])
    }

    yield put({
      type: SEND_EMAIL_UPDATE_RECIPIENTS_SUCCESS,
      payload,
    })
  } catch (e: any) {
    console.log(e.response?.data)
  } finally {
    yield put({
      type: SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
      payload: loadingItem,
    })
  }
}

function* updateAttachments(action: SendEmailActionTypes) {
  try {
    const files: IEmailAttachment[] = action.payload.files || []
    const originalFiles: IEmailAttachment[] = action.payload.originalFiles || []

    let newFiles = files.filter(
      (x) => !originalFiles.some((y) => y.attachmentId === x.attachmentId)
    ) as unknown as File[]

    let attachmentsWithId: IEmailAttachment[] = []
    let invalidFileTypes = false

    if (hasInvalidFileTypes(newFiles as any, unsupportedEmailFileTypes)) {
      yield delay(100) // To allow fileinput to rerender
      newFiles = newFiles.filter(
        (x) => !unsupportedEmailFileTypes.includes(`.${getExtension(x.name)}`)
      )
      invalidFileTypes = true
    }

    if (newFiles.length) {
      // Add the new files
      const formData = new FormData()
      newFiles.map((x: any) => formData.append('files', x))
      yield call(addEmailAttachments, action.payload.id, formData)

      // Get attachments with id
      const { data: updatedEmail } = yield call(getEmail, action.payload.id)
      attachmentsWithId = updatedEmail?.attachments || []
    } else {
      // Delete the file if action is not new files
      const deletedFile = originalFiles.find(
        (x) => !files.some((y) => y.attachmentId === x.attachmentId)
      )
      if (deletedFile)
        yield call(
          deleteEmailAttachment,
          action.payload.id,
          deletedFile?.attachmentId
        )
      attachmentsWithId = [
        ...originalFiles.filter(
          (x) => x.attachmentId !== deletedFile?.attachmentId
        ),
      ]
    }

    yield put({
      type: SEND_EMAIL_UPDATE_ATTACHMENTS_SUCCESS,
      payload: {
        allFiles: attachmentsWithId,
        invalidUploaded: invalidFileTypes,
      },
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_UPDATE_ATTACHMENTS_ERROR,
      payload:
        e.response.data?.Message ||
        'Det skjedde en feil ved lagring av vedlegg',
    })
  }
}

function* deleteDraft(action: SendEmailActionTypes) {
  try {
    yield call(deleteMessage, action.payload)

    yield put({
      type: SEND_EMAIL_DELETE_DRAFT_SUCCESS,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_DELETE_DRAFT_ERROR,
      payload:
        e.response.data?.Message ||
        'Det skjedde en feil ved sletting av utkast',
    })
  }
}

function* createReplyOrForwardDraft(action: SendEmailActionTypes) {
  try {
    let res: { data: string }
    if (action.payload.isForward) {
      res = yield call(createForwardDraft, action.payload.id)
    } else {
      res = yield call(
        createReplyDraft,
        action.payload.id,
        action.payload.isReplyAll
      )
    }

    const { data }: { data: IEmailModel } = yield call(getEmail, res.data)

    const { data: signature }: { data: ISignature } = yield select(
      getEmailSignature
    )
    data.contentHtml = `${newEmailPrefix}${getFullSignature(
      signature?.standardLines,
      signature?.additionalLines
    )}<br/><hr/>${data.contentHtml}`
    yield put({
      type: SEND_EMAIL_REPLY_OR_FORWARD_SUCCESS,
      payload: data,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_REPLY_OR_FORWARD_ERROR,
      payload:
        e.response?.data?.Message ||
        'Det skjedde en feil ved utheting av e-post',
    })
  }
}

function* fetchReciept(action: SendEmailActionTypes) {
  try {
    const { data } = yield call(getEmailReciept, action.payload)

    yield put({
      type: SEND_EMAIL_FETCH_RECIEPT_SUCCESS,
      payload: data,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_FETCH_RECIEPT_ERROR,
      payload: 'Det skjedde en feil ved uthenting av kvittering',
    })
  }
}

/**
 * Will handle signalR message from backend. if Status = success - sets sendingstatus = sent.
 * If error in sending status, display error message
 */
function* handleSendingStatus(action: SendEmailActionTypes) {
  const { messageId: activeRecieptMessageId } = yield select(recieptSelector)
  const messageId = action.payload?.Notification?.Payload?.MessageId
  const sendingStatusPayload: ISendingStatusPayload | undefined =
    action.payload?.Notification?.Payload
  const isActiveReciept = Boolean(
    messageId && messageId === activeRecieptMessageId
  )
  const currentEmailId: string = yield select(getCurrentEmail)

  try {
    if (sendingStatusPayload?.Success) {
      // Fetch new entries in list if on "Sendte" folder
      yield put({
        type: EMAILS_FETCH_NEW_SILENTLY_REQUESTED,
        payload: {
          isOnEmailPage: action.payload.isOnEmailPage,
          section: InboxSectionEnum.Sent,
        },
      })

      if (sendingStatusPayload.Relation?.MessageId) {
        // Mark list item as replied or forwarded
        yield put({
          type: EMAILS_MARK_AS_REPLIED_OR_FORWARDED,
          payload: sendingStatusPayload,
        })

        // Refetch active email if related to new email
        if (sendingStatusPayload.Relation.MessageId === currentEmailId)
          yield put({
            type: EMAIL_FETCH_SILENT_REQUESTED,
          })
      }
    }
    if (isActiveReciept && sendingStatusPayload?.Success) {
      yield put({
        type: SEND_EMAIL_UPDATE_SENDING_STATUS_SUCCESS,
      })
    } else if (
      isActiveReciept &&
      sendingStatusPayload &&
      !sendingStatusPayload.Success
    ) {
      yield put({
        type: SEND_EMAIL_UPDATE_SENDING_STATUS_ERROR,
        payload: 'E-posten ble ikke sendt. Vennligst prøv på ny.',
      })

      yield put(
        addToasterMessage({
          text: 'E-posten ble ikke sendt. Vennligst prøv på ny',
          type: 'danger',
        })
      )
    }
    if (sendingStatusPayload)
      yield put({ type: EMAILS_FETCH_STATISTICS_REQUESTED }) // Fetch failed count on status update
  } catch (e: any) {
    console.log(e.response?.data)
  }
}

function* refetchSendingStatus(action: SendEmailActionTypes) {
  try {
    const { data } = yield call(getEmailReciept, action.payload)

    yield put({
      type: SEND_EMAIL_REFETCH_SENDING_STATUS_SUCCESS,
      payload: data.status,
    })
  } catch (e: any) {
    console.log(e.response?.data)
  }
}

function* createContact(action: SendEmailActionTypes) {
  try {
    const { id } = yield select(getActiveClient)

    yield call(addBoardContact, id, action.payload)
    yield put({
      type: SEND_EMAIL_CREATE_CONTACT_SUCCESS,
      payload: action.payload,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_EMAIL_CREATE_CONTACT_FAILED,
      payload: 'Det skjedde en feil ved oppretting av kontakt',
    })
  }
}

function* removeRecipient(action: SendEmailActionTypes) {
  const payload = action.payload as IMessageRecipient
  const loadingItems: ILoadingMessageRecipient[] = [
    {
      // TODO: update to item.id
      id: payload.nameregisterId || payload.email || nanoid(),
      type: 'remove',
      recipientType: payload.recipientType,
    },
  ]

  yield put({
    type: SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
    payload: loadingItems,
  })

  try {
    const { messageId }: { messageId: string } = yield select(getSendEmail)

    // If in draft mode
    if (messageId) {
      const updateObj = {
        op: 'remove',
        path: '/recipients',
        value: [
          {
            nameregisterId: payload.nameregisterId,
            email: payload.email,
            recipientType: payload.recipientType,
          },
        ],
      } as IPatchModel
      yield call(updateEmailRecipients, messageId, [updateObj])
    }

    yield put({
      type: SEND_EMAIL_REMOVE_RECIPIENT_SUCCESS,
      payload: {
        nameregisterId: payload.nameregisterId,
        email: payload.email ?? undefined,
        recipientType: payload.recipientType,
      },
    })
  } catch (e: any) {
    console.log(e.response?.data)
  } finally {
    yield put({
      type: SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
      payload: loadingItems,
    })
  }
}

function* updateManyRecipients(action: SendEmailActionTypes) {
  const payload = action.payload as IMessageRecipient[]

  let loadingItems = [] as ILoadingMessageRecipient[]

  try {
    const { messageId }: { messageId: string } = yield select(getSendEmail)

    const recipients: IMessageRecipient[] = yield select(getSendEmailRecipients)

    // check if recipients exists in payload
    const payloadRecipientsThatIsNotAdded = payload.filter(
      (x) =>
        !recipients.find(
          (y) =>
            y.recipientType === x.recipientType &&
            ((y.email?.toLowerCase() === x.email?.toLowerCase() &&
              x.nameregisterId === y.nameregisterId) ||
              // Specific for emails the user adds themselves
              (y.email === x.email && x.email !== null))
        )
    )

    if (!payloadRecipientsThatIsNotAdded.length) return

    loadingItems = payloadRecipientsThatIsNotAdded.map(
      (item) =>
        ({
          // TODO: update to item.id
          id: item.nameregisterId || nanoid(),
          type: 'add',
          recipientType: item.recipientType,
        } as ILoadingMessageRecipient)
    )

    yield put({
      type: SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
      payload: loadingItems,
    })

    // If in draft mode
    if (messageId) {
      const updateObj = {
        op: 'add',
        path: '/recipients',
        value: payloadRecipientsThatIsNotAdded,
      } as IPatchModel
      yield call(updateEmailRecipients, messageId, [updateObj])
    }

    yield put({
      type: SEND_EMAIL_UPDATE_MANY_RECIPIENTS_SUCCESS,
      payload: payloadRecipientsThatIsNotAdded,
    })
  } catch (e: any) {
    console.log(e)
  } finally {
    yield put({
      type: SEND_EMAIL_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
      payload: loadingItems,
    })
  }
}

function* sendEmailSaga() {
  yield takeEvery(SEND_EMAIL_REQUESTED, sendEmail)
  yield takeEvery(SEND_EMAIL_CREATE_DRAFT_REQUESTED, createDraft)
  yield takeEvery(SEND_EMAIL_UPDATE_DRAFT_REQUESTED, updateDraft)
  yield takeEvery(SEND_EMAIL_UPDATE_ATTACHMENTS_REQUESTED, updateAttachments)
  yield takeEvery(SEND_EMAIL_DELETE_DRAFT_REQUESTED, deleteDraft)
  yield takeEvery(SEND_EMAIL_UPDATE_RECIPIENTS_REQUESTED, updateRecipient)
  yield takeEvery(
    SEND_EMAIL_UPDATE_MANY_RECIPIENTS_REQUESTED,
    updateManyRecipients
  )
  yield takeEvery(
    SEND_EMAIL_REPLY_OR_FORWARD_REQUESTED,
    createReplyOrForwardDraft
  )

  yield takeEvery(SEND_EMAIL_FETCH_RECIEPT_REQUESTED, fetchReciept)
  yield takeEvery(SEND_EMAIL_CREATE_CONTACT_REQUESTED, createContact)
  yield takeEvery(SEND_EMAIL_REMOVE_RECIPIENT_REQUESTED, removeRecipient)
  yield takeEvery(
    SEND_EMAIL_UPDATE_SENDING_STATUS_REQUESTED,
    handleSendingStatus
  )
  yield takeEvery(
    SEND_EMAIL_REFETCH_SENDING_STATUS_REQUESTED,
    refetchSendingStatus
  )
}

export default sendEmailSaga
